import React, {
  createContext,
  useContext,
  ReactNode,
  useReducer,
  useCallback,
} from 'react';
import {
  EmailNotificationUser,
  NotificationProfile,
  NotificationRegion,
} from '../components/EmailNotification/types';
import {
  Overlay,
  isRectangle,
  isTerraDrawFeature,
} from '../components/EmailNotification/shared/components/NotificationMap/types';
import { userProfile } from '../components/EmailNotification/util';
import { cloneDeep } from 'lodash';

interface State {
  initalized: boolean;
  error: boolean;
  user: EmailNotificationUser | null;
  selectedProfileIndex?: number;
  updateProfile: (updatedProfile: NotificationProfile) => void;
  setError: (error: boolean) => void;
  setUser: (user: EmailNotificationUser | null) => void;
  setSelectedProfileIndex?: (index: number | undefined) => void;
  updateRegions: (overlays: Overlay[]) => void;
  logout: () => void;
}

type Action = {
  type:
    | 'SET_INITALIZED'
    | 'SET_USER'
    | 'SET_ERROR'
    | 'SET_SELECTED_PROFILE_INDEX'
    | 'UPDATE_PROFILE'
    | 'UPDATE_REGIONS';
  payload?:
    | EmailNotificationUser
    | NotificationProfile
    | Overlay[]
    | null
    | boolean
    | number
    | undefined;
};

const initialState: State = {
  initalized: false,
  error: false,
  user: cloneDeep(userProfile) as EmailNotificationUser,
  selectedProfileIndex: 0,
  updateProfile: function (updatedProfile: NotificationProfile): void {
    throw new Error('Function not implemented.');
  },
  setError: (error: boolean) => {
    console.error('setError called with:', error);
  },
  setUser: (user: EmailNotificationUser | null) => {
    console.warn('setUser called with:', user);
  },
  setSelectedProfileIndex: (index: number | undefined) => {
    console.warn('setSelectedProfileIndex called with:', index);
  },
  updateRegions: (overlays: Overlay[]) => {
    console.warn('updateRegions called with:', overlays);
  },
  logout: function (): void {
    throw new Error('Function not implemented.');
  },
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_INITALIZED':
      return { ...state, initalized: action.payload as boolean };
    case 'SET_USER': {
      const newUser = action.payload as EmailNotificationUser | null;
      return {
        ...state,
        user: newUser ? cloneDeep(newUser) : null,
        // Reset selectedProfileIndex if user is changing
        selectedProfileIndex:
          (newUser?.extProfiles?.length ?? 0) > 0 ? 0 : undefined,
      };
    }
    case 'SET_ERROR':
      return { ...state, error: action.payload as boolean };
    case 'SET_SELECTED_PROFILE_INDEX':
      return {
        ...state,
        selectedProfileIndex: action.payload as number | undefined,
      };
    case 'UPDATE_PROFILE': {
      if (!state.user) {
        return state;
      }
      const updatedProfile = action.payload as NotificationProfile;
      const updatedProfiles = state.user.extProfiles
        ? state.user.extProfiles.map((profile, index) => {
            if (index === state.selectedProfileIndex) {
              return cloneDeep({ ...profile, ...updatedProfile });
            }
            return cloneDeep(profile);
          })
        : [];
      return {
        ...state,
        user: { ...state.user, extProfiles: updatedProfiles },
      };
    }
    case 'UPDATE_REGIONS': {
      if (!state.user) {
        return state;
      }
      const updatedRegions = createUpdatedRegions(action.payload as Overlay[]);
      const updatedProfiles = state.user.extProfiles.map((profile, index) => {
        if (index === state.selectedProfileIndex) {
          return cloneDeep({ ...profile, extRegions: updatedRegions });
        }
        return cloneDeep(profile);
      });
      return {
        ...state,
        user: { ...state.user, extProfiles: updatedProfiles },
      };
    }
    default:
      return state;
  }
};

interface LoginContextProps {
  initalized: boolean;
  user: EmailNotificationUser;
  error: boolean;
  setError: (error: boolean) => void;
  selectedProfileIndex?: number;
  setSelectedProfileIndex?: (index: number | undefined) => void;
  updateProfile: (updatedProfile: NotificationProfile) => void;
  setUser: (user: EmailNotificationUser | null) => void;
  updateRegions: (overlays: Overlay[]) => void;
  logout: () => void;
  reloadUser: () => Promise<void>;
}

const LoginContext = createContext<LoginContextProps>({
  initalized: false,
  error: false,
  user: cloneDeep(userProfile) as EmailNotificationUser,
  selectedProfileIndex: 0,
  updateProfile: function (updatedProfile: NotificationProfile): void {
    throw new Error('Function not implemented.');
  },
  setError: (error: boolean) => {
    console.error('setError called with:', error);
  },
  setUser: (user: EmailNotificationUser | null) => {
    console.warn('setUser called with:', user);
  },
  setSelectedProfileIndex: (index: number | undefined) => {
    console.warn('setSelectedProfileIndex called with:', index);
  },
  updateRegions: (overlays: Overlay[]) => {
    console.warn('updateRegions called with:', overlays);
  },
  logout: function (): void {
    throw new Error('Function not implemented.');
  },
  reloadUser: async () => {
    throw new Error('Function not implemented.');
  },
});

export const LoginProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { user, initalized, error, selectedProfileIndex } = state;

  React.useEffect(() => {
    const checkLoggedIn = async () => {
      const res = await fetch(
        import.meta.env.VITE_REACT_APP_API_HOST + '/loggedin',
        { credentials: 'include', redirect: 'follow' }
      );
      res.json().then((res) => {
        if (res.success) {
          dispatch({ type: 'SET_USER', payload: res.object });
        }
        dispatch({ type: 'SET_INITALIZED', payload: true });
      });
    };
    checkLoggedIn();
  }, []);

  const setUser = useCallback((user: EmailNotificationUser | null) => {
    dispatch({ type: 'SET_USER', payload: user });
  }, []);

  const updateRegions = useCallback((overlays: Overlay[]) => {
    dispatch({
      type: 'UPDATE_REGIONS',
      payload: overlays,
    });
  }, []);

  const updateProfile = useCallback((updatedProfile: NotificationProfile) => {
    dispatch({
      type: 'UPDATE_PROFILE',
      payload: updatedProfile,
    });
  }, []);

  const setError = useCallback((error: boolean) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  const setSelectedProfileIndex = useCallback((index: number | undefined) => {
    dispatch({ type: 'SET_SELECTED_PROFILE_INDEX', payload: index });
  }, []);

  // Add reloadUser function
  const reloadUser = useCallback(async () => {
    const res = await fetch(
      import.meta.env.VITE_REACT_APP_API_HOST + '/loggedin',
      { credentials: 'include', redirect: 'follow' }
    );
    const data = await res.json();
    if (data.success) {
      dispatch({ type: 'SET_USER', payload: data.object });
    }
    dispatch({ type: 'SET_INITALIZED', payload: true });
  }, []);

  const logout = useCallback(() => {
    fetch(import.meta.env.VITE_REACT_APP_API_HOST + '/user/logout', {
      method: 'GET',
      credentials: 'include',
    }).then(() => {
      dispatch({ type: 'SET_USER', payload: null });
      setTimeout(() => {
        window.location.href = '/notification';
      }, 5000);
    });
  }, []);

  return (
    <LoginContext.Provider
      value={{
        user: user ?? (cloneDeep(userProfile) as EmailNotificationUser),
        error,
        selectedProfileIndex,
        setSelectedProfileIndex,
        setError,
        setUser,
        initalized,
        updateProfile,
        updateRegions,
        logout,
        reloadUser,
      }}
    >
      {children}
    </LoginContext.Provider>
  );
};

const createUpdatedRegions = (overlays: Overlay[]): NotificationRegion[] => {
  return overlays.map((region) => {
    // Handle Google Maps Rectangle
    if (region.geometry && isRectangle(region.geometry)) {
      return {
        southwest: {
          latitude: region.geometry.getBounds()?.getSouthWest()?.lat() ?? 0,
          longitude: region.geometry.getBounds()?.getSouthWest()?.lng() ?? 0,
        },
        northeast: {
          latitude: region.geometry.getBounds()?.getNorthEast()?.lat() ?? 0,
          longitude: region.geometry.getBounds()?.getNorthEast()?.lng() ?? 0,
        },
      };
    }

    // Handle Terra Draw GeoJSON Feature
    if (region.geometry && isTerraDrawFeature(region.geometry)) {
      const coordinates = region.geometry.geometry.coordinates[0] as number[][];
      // Extract all latitudes and longitudes
      const lats = coordinates.map((coord) => coord[1]);
      const lngs = coordinates.map((coord) => coord[0]);

      return {
        southwest: {
          latitude: Math.min(...lats),
          longitude: Math.min(...lngs),
        },
        northeast: {
          latitude: Math.max(...lats),
          longitude: Math.max(...lngs),
        },
      };
    }

    // Fallback for undefined geometry
    return {
      southwest: { latitude: 0, longitude: 0 },
      northeast: { latitude: 0, longitude: 0 },
    };
  }) as NotificationRegion[];
};

export const useLoginContext = (): LoginContextProps => {
  const context = useContext(LoginContext);
  if (!context) {
    throw new Error('useLoginContext must be used within a LoginProvider');
  }
  return context;
};
